import { Alert, Button, Form, Popconfirm } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { ActionEnum, AlertMessageEnum } from '../common'
import { translate } from '../languages'
import { ApiMethodEnum, UserTypeEnum } from '../models'
import { RootState } from '../stores'
import { setAlert, setShowManageModal } from '../stores/actions'

type ManageModalFooterComponentProps = {
  onDeleteConfirm?: () => void
  hasDefaultButtons?: boolean
  customButtons?: React.ReactElement
  onClose?: () => void
  closeText?: boolean
}

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

export const ManageModalFooterComponent: React.FC<ManageModalFooterComponentProps> =
  ({
    onDeleteConfirm,
    hasDefaultButtons,
    customButtons,
    onClose,
    closeText,
  }) => {
    const dispatch = useDispatch()
    const location = useLocation()
    const { management } = useSelector((state: RootState) => state.common)
    const { language } = useSelector((state: RootState) => state.language)
    const { profile } = useSelector((state: RootState) => state.auth)
    const { apis } = useSelector((state: RootState) => state.api)

    const defaultButtons = React.useMemo(() => {
      switch (management.action) {
        case ActionEnum.VIEW:
          return (
            <Button
              htmlType="submit"
              type="primary"
              onClick={() => dispatch(setAlert())}
              style={{ marginLeft: '10px' }}
            >
              {translate('common.edit', language)}
            </Button>
          )
        case ActionEnum.UPDATE:
          return (
            <Button
              htmlType="submit"
              type="primary"
              loading={management.actionProcessing}
              onClick={() => dispatch(setAlert())}
            >
              {translate(
                `common.${management.actionProcessing ? 'updating' : 'update'}`,
                language
              )}
            </Button>
          )
        case ActionEnum.CREATE:
          return (
            <Button
              htmlType="submit"
              type="primary"
              loading={management.actionProcessing}
              onClick={() => {
                dispatch(setAlert())
              }}
            >
              {translate(
                `common.${management.actionProcessing ? 'creating' : 'create'}`,
                language
              )}
            </Button>
          )
        case ActionEnum.DELETE:
          return (
            <Popconfirm
              title={translate('common.delete.confirm', language)}
              okText={translate('common.yes', language)}
              cancelText={translate('common.no', language)}
              placement="topRight"
              onConfirm={() => {
                dispatch(setAlert())
                onDeleteConfirm && onDeleteConfirm()
              }}
            >
              <Button
                htmlType="submit"
                type="primary"
                loading={management.actionProcessing}
              >
                {translate(
                  `common.${
                    management.actionProcessing ? 'deleting' : 'delete'
                  }`,
                  language
                )}
              </Button>
            </Popconfirm>
          )
        default:
          return <></>
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, management])

    const getAlertType = () => {
      if (management.alertMessage) {
        switch (management.alertMessage.type) {
          case AlertMessageEnum.ERROR:
            return 'error'
          case AlertMessageEnum.WARNING:
            return 'warning'
          case AlertMessageEnum.SUCCESS:
            return 'success'
          case AlertMessageEnum.INFO:
            return 'info'
          default:
            return 'info'
        }
      }
      return 'info'
    }

    return (
      <>
        {management.alertMessage && (
          <StyledFormItem>
            <Alert
              message={translate(`common.alert.${getAlertType()}`, language)}
              description={management.alertMessage.message}
              type={getAlertType()}
              showIcon
            />
          </StyledFormItem>
        )}
        <StyledFormItem>
          <div style={{ float: 'right' }}>
            <Button
              type="ghost"
              style={{
                outline: 'none',
                marginRight: '10px',
              }}
              disabled={management.actionProcessing}
              onClick={
                onClose
                  ? () => onClose()
                  : () => {
                      dispatch(setShowManageModal(false))
                      dispatch(setAlert())
                    }
              }
            >
              {translate('common.cancel', language)}
            </Button>
            {customButtons}
            {profile &&
              (profile.userType.type === UserTypeEnum.ADMIN ||
                apis.filter(
                  (item) =>
                    item.path === location.pathname &&
                    [ApiMethodEnum.PUT, ApiMethodEnum.DELETE].includes(
                      item.method
                    )
                ).length > 0) &&
              (!management.alertMessage ||
                (management.alertMessage &&
                  management.alertMessage.type !== AlertMessageEnum.SUCCESS)) &&
              hasDefaultButtons &&
              defaultButtons}
          </div>
        </StyledFormItem>
      </>
    )
  }
